import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DollarSign, Calendar, CheckCircle, XCircle, Clock, ArrowRightLeft, MessageSquare } from 'lucide-react';
import { CounterOffer } from '@/hooks/useCounterOffers';
import { format } from 'date-fns';
import { useUser } from '@/contexts/UserContext';

interface CounterOfferThreadProps {
  counterOffers: CounterOffer[];
  onAccept?: (counterId: string) => void;
  onReject?: (counterId: string) => void;
  onCounter?: () => void;
  loading?: boolean;
}

export const CounterOfferThread: React.FC<CounterOfferThreadProps> = ({
  counterOffers,
  onAccept,
  onReject,
  onCounter,
  loading,
}) => {
  const { currentUser } = useUser();

  const statusConfig: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
    pending: { icon: <Clock className="h-3 w-3" />, className: 'bg-warning/10 text-warning border-warning/20', label: 'Pending' },
    accepted: { icon: <CheckCircle className="h-3 w-3" />, className: 'bg-green-500/10 text-green-700 border-green-200', label: 'Accepted' },
    rejected: { icon: <XCircle className="h-3 w-3" />, className: 'bg-destructive/10 text-destructive border-destructive/20', label: 'Rejected' },
    superseded: { icon: <ArrowRightLeft className="h-3 w-3" />, className: 'bg-muted text-muted-foreground', label: 'Superseded' },
  };

  if (counterOffers.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        No counter offers yet. Start negotiating by proposing new terms.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {counterOffers.map((offer) => {
        const isOwn = offer.proposed_by_profile_id === currentUser?.id;
        const status = statusConfig[offer.status];
        const isPending = offer.status === 'pending';
        const canRespond = isPending && !isOwn;

        return (
          <Card key={offer.id} className={`${isOwn ? 'ml-4' : 'mr-4'} border`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {offer.proposed_by_role}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(offer.created_at), 'MMM dd, yyyy h:mm a')}
                  </span>
                </div>
                <Badge variant="outline" className={`text-xs flex items-center gap-1 ${status.className}`}>
                  {status.icon}
                  {status.label}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold">${offer.proposed_value.toLocaleString()}</span>
                </div>
                {offer.proposed_deadline && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{format(new Date(offer.proposed_deadline), 'MMM dd, yyyy')}</span>
                  </div>
                )}
                {offer.proposed_deal_type && (
                  <div className="text-muted-foreground">
                    Type: <span className="text-foreground capitalize">{offer.proposed_deal_type}</span>
                  </div>
                )}
              </div>

              {offer.proposed_terms && (
                <div className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Terms:</strong> {offer.proposed_terms}
                </div>
              )}

              {offer.message && (
                <div className="flex items-start gap-2 text-sm bg-muted/50 rounded-md p-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span>{offer.message}</span>
                </div>
              )}

              {canRespond && (
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    onClick={() => onAccept?.(offer.id)}
                    disabled={loading}
                    className="flex items-center gap-1"
                  >
                    <CheckCircle className="h-3 w-3" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => onReject?.(offer.id)}
                    disabled={loading}
                    className="flex items-center gap-1"
                  >
                    <XCircle className="h-3 w-3" />
                    Reject
                  </Button>
                  {/* Only show Counter when caller passes a real handler.
                      CounterOfferModal renders this thread INSIDE the counter
                      flow itself, so the button there would be a no-op. */}
                  {onCounter && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onCounter}
                      disabled={loading}
                      className="flex items-center gap-1"
                    >
                      <ArrowRightLeft className="h-3 w-3" />
                      Counter
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
